import { message as messageAntd } from 'antd'

import { PRICING_TYPE_ENUM, ADDRESS_PROPERTY_TYPE_ENUM } from '@24slg/database-schemas/enums'
import saleOrderGroupUtil from '@24slg/common-utilities/saleOrderGroup/withDb'

import _compact from 'lodash/compact'
import _filter from 'lodash/filter'
import _join from 'lodash/join'
import _get from 'lodash/get'
import _isEmpty from 'lodash/isEmpty'
import _isNaN from 'lodash/isNaN'
import _map from 'lodash/map'
import _size from 'lodash/size'
import _split from 'lodash/split'
import _startsWith from 'lodash/startsWith'
import _toUpper from 'lodash/toUpper'
import _trim from 'lodash/trim'
import _isEqual from 'lodash/isEqual'
import _some from 'lodash/some'

import Icon from 'components/Icon'

import dayjs from 'dayjs'
import { setCookie, getCookie } from 'utils/cookie'

export const responseSuccess = ({ data }) => {
  return {
    status: 'success',
    data,
  }
}

export const responseError = ({ message, error, showPopupError = false }) => {
  let displayMessage = message

  if (!message) {
    if (error?.request) {
      displayMessage = 'ขออภัยระบบขัดข้อง กรุณาทำรายการใหม่อีกครั้งในอีก 5 นาที'
    } else {
      displayMessage = error?.message
    }
  }

  if (showPopupError) {
    messageAntd.error(displayMessage)
  }

  return {
    status: 'error',
    message: displayMessage,
  }
}

export const addressDisplay = value => {
  const address = value?.address
  const subDistrict = value?.subDistrict
  const district = value?.district
  const province = value?.province
  const postcode = value?.postalCode

  return _compact([address, subDistrict, district, province, postcode]).join(' ')
}

export const getAvaliableTimes = ({ availableDates, date }) => {
  const filterTimes = _filter(availableDates, aDate => dayjs(aDate).isSame(date, 'day'))
  const mapfieldTimes = _map(filterTimes, item => {
    const time = dayjs(item).format('HH:mm')
    return {
      label: time,
      value: time,
    }
  })

  return mapfieldTimes
}

export const getAddressIcon = ({ slug, fontSize }) => {
  let icon = null

  if (slug === ADDRESS_PROPERTY_TYPE_ENUM.HOUSE) {
    icon = <Icon.Home style={{ fontSize: fontSize || 20 }} />
  } else if (slug === ADDRESS_PROPERTY_TYPE_ENUM.CONDOMINIUM) {
    icon = <Icon.Hotel style={{ fontSize: fontSize || 18 }} />
  } else if (slug === ADDRESS_PROPERTY_TYPE_ENUM.OFFICE_BUILDING) {
    icon = <Icon.Office style={{ fontSize: fontSize || 18 }} />
  } else if (slug === ADDRESS_PROPERTY_TYPE_ENUM.TOWNHOUSE_TOWNHOME) {
    icon = <Icon.Home style={{ fontSize: fontSize || 20 }} />
  } else if (slug === ADDRESS_PROPERTY_TYPE_ENUM.COMMERCIAL_BUILDING) {
    icon = <Icon.Office style={{ fontSize: fontSize || 18 }} />
  } else if (slug === ADDRESS_PROPERTY_TYPE_ENUM.DEPARTMENT_STORE) {
    icon = <Icon.Office style={{ fontSize: fontSize || 18 }} />
  } else {
    icon = <Icon.Home style={{ fontSize: fontSize || 20 }} />
  }

  return icon
}

const clearNumber = (value = '') => {
  return value.replace(/\D+/g, '')
}

export const formatExpirationDate = (value, keyValue) => {
  const clearValue = clearNumber(value)

  if (keyValue === 'Backspace') {
    if (_size(value) <= 3) {
      return clearValue
    }
  }

  if (_size(clearValue) === 1) {
    if (_startsWith(clearValue, '0')) {
      return '0'
    }
    if (clearValue !== '1') {
      return `0${clearValue}/`
    }
  }

  if (_size(clearValue) >= 2) {
    return `${clearValue.slice(0, 2)}/${clearValue.slice(2, 4)}`
  }

  return clearValue
}

export const formatCreditCardNumber = value => {
  if (!value) {
    return value
  }

  const clearValue = clearNumber(value)

  const nextValue = `${clearValue.slice(0, 4)} ${clearValue.slice(4, 8)} ${clearValue.slice(
    8,
    12
  )} ${clearValue.slice(12, 19)}`

  return nextValue.trim()
}

export const mergeFullName = (firstname, lastname) => {
  const items = _compact([firstname, lastname])
  return _join(items, ' ')
}

export const scrollToElement = (element, offsetTop = 0) => {
  if (typeof document === 'undefined') {
    return false
  }

  const targetElement = document.querySelector(element)

  if (!targetElement) {
    return false
  }

  const elementOffsetTop = targetElement.offsetTop || 0
  const top = Math.max(elementOffsetTop - offsetTop, 0)

  window.scroll({
    top,
    left: 0,
    behavior: 'smooth',
  })

  return true
}

export const updateObjectArrayByIndex = ({ oldDatas, newData, updateIndex }) => {
  const newDatas = [...oldDatas]

  if (newDatas[updateIndex]) {
    newDatas[updateIndex] = {
      ...newDatas[updateIndex],
      ...newData,
    }
  }

  return newDatas
}

export const formatPhoneNumber = str => {
  const cleaned = ('' + str).replace(/\D/g, '')
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    return match[1] + '-' + match[2] + '-' + match[3]
  }
  return null
}

export const mapfieldFile = values => _map(values, value => value?.originFileObj)

export const alphabetPosition = index => {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz'
  return _toUpper(_get(alphabet, index))
}

export const removeEmpty = obj => {
  for (var propName in obj) {
    if (obj[propName] === null || obj[propName] === undefined) {
      delete obj[propName]
    }
  }
  return obj
}

export const displayBookingDateTimeRange = dateTimeRange => {
  const DATE_FORMAT = 'DD MMMM YYYY'
  const bookingDateTimeRangeSplit = _split(dateTimeRange, ',')

  let bookingDate = bookingDateTimeRangeSplit[0]

  if (dayjs(bookingDate, DATE_FORMAT).format(DATE_FORMAT) !== bookingDate) {
    bookingDate = dayjs(bookingDate, 'DD/MM/YYYY').format('DD MMMM YYYY')
  }

  const bookingTimeRange = bookingDateTimeRangeSplit[1]

  const bookingDateTimeRangeDisplay = `${bookingDate}, ${bookingTimeRange}`

  const times = _split(bookingTimeRange, '-')

  let display = ''
  if (bookingTimeRange) {
    if (_trim(times?.[0]) === _trim(times?.[1])) {
      const bookingTimeDisplay = `${times?.[0]} น.`
      display = `${bookingDate} ${bookingTimeDisplay}`
    } else {
      display = `${bookingDateTimeRangeDisplay} น.`
    }
  }

  return display
}

export const displayBookingTime = bookingTimes => {
  const times = _split(bookingTimes, '-')

  let display = `${bookingTimes} น.`
  if (_trim(times?.[0]) === _trim(times?.[1])) {
    display = `${times?.[0]} น.`
  }

  return display
}

export const getGASessionId = () => {
  const gaSessionIdCookie = '_gasessionid'

  let sessionId = getCookie(gaSessionIdCookie)

  if (_isEmpty(sessionId)) {
    const timestamp = dayjs().format('x')
    const newSessionId = timestamp

    setCookie(gaSessionIdCookie, newSessionId, 1800) // 30min

    sessionId = newSessionId
  }

  return sessionId
}

export const getNumberForQty = qty => {
  if (_isNaN(Number(qty)) || Number(qty) === 0) {
    return 1
  }
  return Number(qty)
}

export const getProductDetail = (item, quantity = 1) => {
  const saleOrderGroup = {}
  const saleOrderGroupItem = {
    sellingPrice: item,
    quantity,
    pricingType: PRICING_TYPE_ENUM.CONTRACT,
  }

  const unitPrice = saleOrderGroupUtil.item.getUnitPrice(saleOrderGroup, saleOrderGroupItem)

  const amount = saleOrderGroupUtil.item.calculateAmountAfterUnitDiscount(
    saleOrderGroup,
    saleOrderGroupItem
  )

  const uom = item?.customerUom || item?.product?.uom

  return { unitPrice, amount, uom }
}

export const mergeFields = (fields, separator = ' ') => {
  return _join(_compact(fields), separator)
}

export const handleErrorMessage = error => {
  const responseData = error?.response?.data

  if (responseData) {
    return mergeFields([responseData?.message, responseData?.error], ': ')
  }

  return error?.message || 'ระบบขัดข้อง กรุณารอสักครู่ และลองใหม่อีกครั้ง'
}

export const isValuesChange = (prevValues, curValues, fields) => {
  return _some(fields, field => {
    return !_isEqual(_get(prevValues, field), _get(curValues, field))
  })
}

export const cleanDataFromDuplicateForCreate = data => {
  if (data) {
    delete data.id
    delete data.runningId
    delete data.createdAt
    delete data.createdById
    delete data.updatedAt
    delete data.updatedById
    delete data.deletedAt
    delete data.deletedById
  }

  return data
}
